// Imports ------------------------------------------------------------------->

import {Animation} from "../../utils/Animation";
import {Sample} from "./InputSampler";

// Private Definitions ------------------------------------------------------->

interface FlyerPointerEvent {
	pointerId: number;
	clientX: number;
	clientY: number;
	pointerType: string;
}

// Public Definitions -------------------------------------------------------->

export namespace FlyerController {
	export interface PointerState {
		clientX: number;
		clientY: number;
		startX: number;
		startY: number;
		deltaX: number;
		deltaY: number;
	}
}

export const enum FlyerControllerState {
	IDLE, POINTER_DOWN, DRAGGING, ANIMATING,
	WAITING_FOR_DOUBLE_TAP, DOUBLE_TAP_POINTER_DOWN,
	PINCH_DRAGGING
}

// A FlyerController uses input samples to update the positional
// state that a FlyerView should have.
export class FlyerController {
	scrollLeft: number = 0;
	scrollTop: number = 0;
	scrollWidth: number = 0;
	scrollHeight: number = 0;
	viewportWidth: number = 0;
	viewportHeight: number = 0;

	onstatechange: (controller: FlyerController, oldState: FlyerControllerState) => void;
	onboundschange: (controller: FlyerController) => void;
	onhover: (controller: FlyerController, x: number, y: number) => void;
	onzoompress: (controller: FlyerController) => void;
	onclick: (controller: FlyerController, x: number, y: number) => void;

	private _scale: number = 1;
	private _minimumScale: number = 1;
	private _maximumScale: number = 1;

	private _animation: Animation;
	private _state: FlyerControllerState =
			FlyerControllerState.IDLE;
	private _activePointers: {
		[index: string]: FlyerController.PointerState;
	} = {};

	private _fstPointer: number = null;
	private _sndPointer: number = null;
	private _clientX: number = null;
	private _clientY: number = null;
	private _deltaX: number = null;
	private _deltaY: number = null;
	private _distance: number = null;

	private _dragTimeout: number;
	private _wheelTimeout: number;
	private _clickTimeout: number;

	private _pointers: { [key: string]: Sample.PointerState } = {};
	private _lastHoverX: number;
	private _lastHoverY: number;
	private _zoomPressed = false;

	update(sample: Sample, hoverOnly: boolean) {
		if (sample.wheelDeltaX !== 0 || sample.wheelDeltaY !== 0)
			if (!hoverOnly)
				this._handleWheel(sample.wheelDeltaX, sample.wheelDeltaY);

		if (sample.keys[90] && !this._zoomPressed) {
			this._zoomPressed = true;
		} else if (!sample.keys[90] && this._zoomPressed) {
			this._zoomPressed = false;

			if (this.onzoompress)
				this.onzoompress(this);
		}

		const keyboardMoveSensitivity = 20 / this.scale;
		const keyboardZoomSensitivity = 0.01;
		const oldLeft = this.scrollLeft;
		const oldTop = this.scrollTop;
		const oldScale = this.scale;
		this.scale += sample.keys[187] ? keyboardZoomSensitivity : 0;
		this.scale -= sample.keys[189] ? keyboardZoomSensitivity : 0;
		this.scrollLeft -= sample.keys[37] ? keyboardMoveSensitivity : 0;
		this.scrollLeft += sample.keys[39] ? keyboardMoveSensitivity : 0;
		this.scrollTop += sample.keys[40] ? keyboardMoveSensitivity : 0;
		this.scrollTop -= sample.keys[38] ? keyboardMoveSensitivity : 0;

		if (oldLeft !== this.scrollLeft || oldTop !== this.scrollTop ||
			oldScale !== this.scale) {
			if (this.scale < this.minimumScale)
				this.scale = this.minimumScale;
			if (this.scale > this.maximumScale)
				this.scale = this.maximumScale;

			const deltaX = this.scrollLeft - oldLeft;
			const deltaY = this.scrollTop - oldTop;
			const centerX = oldLeft + this.viewportWidth / oldScale / 2;
			const centerY = oldTop + this.viewportHeight / oldScale / 2;

			this.scrollLeft = deltaX + centerX - this.viewportWidth / this.scale / 2;
			this.scrollTop = deltaY + centerY - this.viewportHeight / this.scale / 2;

			this._clampScroll();
			if (this.onboundschange)
				this.onboundschange(this);
		}

		const oldPointers = this._pointers;
		this._pointers = {};
		for (const k in sample.pointers) {
			if (!sample.pointers.hasOwnProperty(k))
				continue;

			const pointer = sample.pointers[k];
			const oldPointer = oldPointers[k];
			const down = !oldPointer;

			if (down && (pointer.buttons & Sample.Button.LEFT)) {
				this._pointers[k] = pointer;
				if (!hoverOnly) {
					this._handleDown({
						pointerId: Number(k),
						clientX: pointer.x,
						clientY: pointer.y,
						pointerType: pointer.kind
					});
				}
			} else if (pointer.buttons & Sample.Button.LEFT) {
				this._pointers[k] = pointer;
				if (oldPointer.x !== pointer.x ||
						oldPointer.y !== pointer.y) {
					if (!hoverOnly) {
						this._handleMove({
							pointerId: Number(k),
							clientX: pointer.x,
							clientY: pointer.y,
							pointerType: pointer.kind
						});
					}
				}
			} else if (pointer.buttons === 0) {
				if (this._lastHoverX !== pointer.x ||
						this._lastHoverY !== pointer.y) {
					this._lastHoverX = pointer.x;
					this._lastHoverY = pointer.y;
					this._handleHover({
						pointerId: Number(k),
						clientX: pointer.x,
						clientY: pointer.y,
						pointerType: pointer.kind
					});
				}
			}
		}

		for (const k in oldPointers) {
			if (!oldPointers.hasOwnProperty(k))
				continue;

			const pointer = oldPointers[k];
			const up = !this._pointers[k];
			if (up && !hoverOnly) {
				this._handleUp({
					pointerId: Number(k),
					clientX: pointer.x,
					clientY: pointer.y,
					pointerType: pointer.kind
				});
			}
		}
	}

	get scale() {
		return this._scale;
	}

	set scale(value: number) {
		if (isNaN(value) || this._scale === value)
			return;

		this._scale = value;
		this._clampScroll();
	}

	get minimumScale() {
		return this._minimumScale;
	}

	set minimumScale(value: number) {
		if (isNaN(value))
			return;

		this._minimumScale = value;
		this._clampScroll();
		if (this.onboundschange)
			this.onboundschange(this);
	}

	get maximumScale() {
		return this._maximumScale;
	}

	set maximumScale(value: number) {
		if (isNaN(value))
			return;

		this._maximumScale = value;
		this._clampScroll();
		if (this.onboundschange)
			this.onboundschange(this);
	}

	get state() {
		return this._state;
	}

	set state(value) {
		const oldState = this._state;
		this._state = value;
		if (oldState !== value && this.onstatechange)
			this.onstatechange(this, oldState);
	}

	get scrollRight() {
		return this.scrollWidth - this.viewportWidth / this.scale;
	}

	get scrollBottom() {
		return this.scrollHeight - this.viewportHeight / this.scale;
	}

	get canScrollX() {
		return this.viewportWidth / this.scale < this.scrollWidth;
	}

	get canScrollY() {
		return this.viewportHeight / this.scale < this.scrollHeight;
	}

	setZoomScaleAnimated(scale: number) {
		const target = scale;
		const focusPoint = [
			this.scrollLeft + this.viewportWidth / this.scale / 2,
			this.scrollTop + this.viewportHeight / this.scale / 2
		];
		const focusPct = [0.5, 0.5];

		const finalWidth = this.viewportWidth / target;
		const finalHeight = this.viewportHeight / target;
		const finalX = focusPoint[0] - focusPct[0] * finalWidth;
		const finalY = focusPoint[1] - focusPct[1] * finalHeight;
		this.zoomToRect(finalX, finalY, finalWidth, finalHeight, true);
	}

	zoomToRect(left: number, top: number, width: number, height: number, animate: boolean) {
		const target = Math.max(
			this.minimumScale, Math.min(
				Math.min(this.viewportWidth / width,
								this.viewportHeight / height),
				this.maximumScale));
		const finalWidth = this.viewportWidth / target;
		const finalHeight = this.viewportHeight / target;
		let finalX = left;
		let finalY = top;
		const finalScrollRight = this.scrollWidth - finalWidth;
		const finalScrollBottom = this.scrollHeight - finalHeight;

		if (width < finalWidth)
			finalX -= (finalWidth - width) / 2;
		if (height < finalHeight)
			finalY -= (finalHeight - height) / 2;

		const finalCenterOffsetX =
				Math.max(0,
						(this.viewportWidth - this.scrollWidth * target) / 2);
		if (finalCenterOffsetX !== 0)
			finalX = -finalCenterOffsetX / target;
		else
			finalX = Math.max(0, Math.min(finalX, finalScrollRight));

		const finalCenterOffsetY =
				Math.max(0,
						(this.viewportHeight - this.scrollHeight * target) / 2);
		if (finalCenterOffsetY !== 0)
			finalY = -finalCenterOffsetY / target;
		else
			finalY = Math.max(0, Math.min(finalY, finalScrollBottom));

		const startWidth = this.viewportWidth / this.scale;
		const startHeight = this.viewportHeight / this.scale;
		const startCenterX = this.scrollLeft + startWidth / 2;
		const startCenterY = this.scrollTop + startHeight / 2;
		const finalCenterX = finalX + finalWidth / 2;
		const finalCenterY = finalY + finalHeight / 2;

		const customFunction = (frac: number) => {
			const centerX = startCenterX + (finalCenterX - startCenterX) * frac;
			const centerY = startCenterY + (finalCenterY - startCenterY) * frac;
			const width = startWidth + (finalWidth - startWidth) * frac;
			const height = startHeight + (finalHeight - startHeight) * frac;

			const newScrollLeft = centerX - width / 2;
			const newScrollTop = centerY - height / 2;
			this.scale = this.viewportHeight / height;

			this.scrollLeft = newScrollLeft;
			this.scrollTop = newScrollTop;

			if (this.onboundschange)
				this.onboundschange(this);
		};
		if (this._animation)
			this._animation.stop();
		this._animation = new Animation(animate ? 200 : 0, "ease-in-out",
																		customFunction);
		this._animation.onfinish = () => {
			if (this.state !== FlyerControllerState.ANIMATING)
				return;

			this.state = FlyerControllerState.IDLE;
			this._clampScroll();
		};
		this._animation.play();
		this.state = FlyerControllerState.ANIMATING;
	}

	private _handleDoubleClick = (clientX: number, clientY: number) => {
		const target = this.scale !== this.minimumScale ?
				this.minimumScale : this.maximumScale;
		const focusPoint = [
			clientX / this.scale + this.scrollLeft,
			clientY / this.scale + this.scrollTop
		];
		const focusPct = [
			clientX / this.viewportWidth,
			clientY / this.viewportHeight
		];

		const finalWidth = this.viewportWidth / target;
		const finalHeight = this.viewportHeight / target;
		const finalX = focusPoint[0] - focusPct[0] * finalWidth;
		const finalY = focusPoint[1] - focusPct[1] * finalHeight;
		this.zoomToRect(finalX, finalY, finalWidth, finalHeight, true);
	}

	private _handleClick = (clientX: number, clientY: number) => {
		if (this.state !== FlyerControllerState.WAITING_FOR_DOUBLE_TAP)
			return;

		if (this.onclick)
			this.onclick(this, clientX, clientY);
	}

	private _handleHover = (e: FlyerPointerEvent) => {
		if (e.pointerType !== "mouse")
			return;

		if (this.onhover)
			this.onhover(this, e.clientX, e.clientY);
	}

	private _handleUp = (e: FlyerPointerEvent) => {
		// Remove the pointers that ended from the active set.
		this._forEachPointer(e, (clientX, clientY, identifier) => {
			if (identifier === this._fstPointer)
				this._fstPointer = null;
			if (identifier === this._sndPointer)
				this._sndPointer = null;
			delete this._activePointers[identifier.toString()];
		});

		// If the primary and secondary pointers didn't change, there's nothing
		// left to do; continue listening.
		if (this._fstPointer !== null &&
				this._sndPointer !== null)
			return;

		// Otherwise, select new primary or secondary identifiers from any other
		// active touches and shuffle around the identifiers.
		if (this._fstPointer === null) {
			this._fstPointer = this._sndPointer;
			this._sndPointer = null;
		}
		if (this._fstPointer === null) {
			for (const k in this._activePointers) {
				if (!this._activePointers.hasOwnProperty(k))
					continue;

				const n = Number(k);
				this._fstPointer = n;
				break;
			}
		}
		if (this._sndPointer === null) {
			for (const k in this._activePointers) {
				if (!this._activePointers.hasOwnProperty(k))
					continue;

				const n = Number(k);
				if (n !== this._fstPointer) {
					this._sndPointer = n;
					break;
				}
			}
		}

		const numPointers = Object.keys(this._activePointers).length;
		if (numPointers > 0) {
			// If there are still active touches, reset the averaged client
			// coordinates and distance to avoid discontinuous jumps on the
			// next move event.
			const primaryPointer =
					this._activePointers[this._fstPointer.toString()];
			const secondaryPointer = numPointers > 1 ?
					this._activePointers[this._sndPointer.toString()] :
					primaryPointer;

			this._deltaX = 0;
			this._deltaY = 0;
			this._clientX =
					(primaryPointer.clientX + secondaryPointer.clientX) / 2;
			this._clientY =
					(primaryPointer.clientY + secondaryPointer.clientY) / 2;
			this._distance = Math.sqrt(
				(primaryPointer.clientX - secondaryPointer.clientX) ** 2 +
				(primaryPointer.clientY - secondaryPointer.clientY) ** 2);

			// Demote to draggign state if needed.
			if (this.state === FlyerControllerState.PINCH_DRAGGING &&
					numPointers === 1)
				this.state = FlyerControllerState.DRAGGING;
			return;
		}

		clearTimeout(this._dragTimeout);

		if (this.scale < this.minimumScale || this.scale > this.maximumScale) {
			const target = this.scale < this.minimumScale ?
					this.minimumScale : this.maximumScale;
			const focusPoint = [
				this._clientX / this.scale + this.scrollLeft,
				this._clientY / this.scale + this.scrollTop,
			];
			const focusPct = [
				this._clientX / this.viewportWidth,
				this._clientY / this.viewportHeight
			];
			const finalWidth = this.viewportWidth / target;
			const finalHeight = this.viewportHeight / target;
			const finalX = focusPoint[0] - focusPct[0] * finalWidth;
			const finalY = focusPoint[1] - focusPct[1] * finalHeight;
			this.zoomToRect(finalX, finalY, finalWidth, finalHeight, true);
		} else {
			const centerOffsetX =
					Math.max(0, (this.viewportWidth -
											this.scrollWidth * this.scale) / 2);
			const outOfBoundsX = centerOffsetX === 0 &&
					(this.scrollLeft < 0 ||
					this.scrollLeft > Math.max(0, this.scrollWidth -
																			this.viewportWidth / this.scale));
			const centerOffsetY =
					Math.max(0, (this.viewportHeight -
											this.scrollHeight * this.scale) / 2);
			const outOfBoundsY = centerOffsetY === 0 &&
					(this.scrollTop < 0 ||
					this.scrollTop > Math.max(this.scrollHeight -
																		this.viewportHeight / this.scale));
			if (outOfBoundsX || outOfBoundsY) {
				this.zoomToRect(this.scrollLeft, this.scrollTop,
												this.viewportWidth / this.scale,
												this.viewportHeight / this.scale, true);
				return;
			} else if (this.state === FlyerControllerState.DRAGGING) {
				if (this._animation)
					this._animation.stop();
				this._animation = new Animation(450, "ease-out", (frac: number) => {
						const boundsRatioX = this.scrollLeft < 0 ||
								this.scrollLeft > this.scrollWidth - this.viewportWidth /
								this.scale ? 0.2 : 1;
						const boundsRatioY = this.scrollTop < 0 ||
								this.scrollTop > this.scrollHeight - this.viewportHeight /
								this.scale ? 0.2 : 1;

						const step = Math.sin(1 - frac);
						this.scrollLeft +=
						this._deltaX / this.scale * step * boundsRatioX;
						this.scrollTop +=
						this._deltaY / this.scale * step * boundsRatioY;
						this._clampScroll();
						if (this.onboundschange)
							this.onboundschange(this);
				});
				this._animation.play();
				this._animation.onfinish = () => {
					if (this.state !== FlyerControllerState.ANIMATING)
						return;

					this.state = FlyerControllerState.IDLE;
				};
				this.state = FlyerControllerState.ANIMATING;
				return;
			}
		}

		if (this.state === FlyerControllerState.POINTER_DOWN) {
			// Start click timeout
			this._clickTimeout = setTimeout(() => {
				if (this.state !== FlyerControllerState.DOUBLE_TAP_POINTER_DOWN) {
					this._handleClick(this._clientX, this._clientY);
					this.state = FlyerControllerState.IDLE;
				}
			}, 200);
			this.state = FlyerControllerState.WAITING_FOR_DOUBLE_TAP;
		} else if (this.state ===
							FlyerControllerState.DOUBLE_TAP_POINTER_DOWN) {
			clearTimeout(this._clickTimeout);
			this._handleDoubleClick(this._clientX, this._clientY);
			this.state = FlyerControllerState.IDLE;
		} else {
			this.state = FlyerControllerState.IDLE;
		}
	}

	private _handleMove = (e: FlyerPointerEvent) => {
		const numPointers = Object.keys(this._activePointers).length;
		const boundsRatioX = this.scrollLeft < 0 ||
				this.scrollLeft > this.scrollWidth - this.viewportWidth /
				this.scale ? 0.2 : 1;
		const boundsRatioY = this.scrollTop < 0 ||
				this.scrollTop > this.scrollHeight - this.viewportHeight /
				this.scale ? 0.2 : 1;

		this._forEachPointer(e, (clientX, clientY, identifier) => {
			const pointer = this._activePointers[identifier.toString()];
			pointer.deltaX = (pointer.clientX - clientX) * boundsRatioX;
			pointer.deltaY = (pointer.clientY - clientY) * boundsRatioY;
			pointer.clientX = clientX;
			pointer.clientY = clientY;
		});

		const primaryPointer =
				this._activePointers[this._fstPointer.toString()];
		const secondaryPointer = numPointers > 1 ?
				this._activePointers[this._sndPointer.toString()] :
				primaryPointer;

		const centroidX =
				(primaryPointer.clientX + secondaryPointer.clientX) / 2;
		const centroidY =
				(primaryPointer.clientY + secondaryPointer.clientY) / 2;
		this._deltaX = (this._clientX - centroidX) * boundsRatioX;
		this._deltaY = (this._clientY - centroidY) * boundsRatioY;
		this._clientX = centroidX;
		this._clientY = centroidY;
		const distance = Math.sqrt(
				(primaryPointer.clientX - secondaryPointer.clientX) ** 2 +
				(primaryPointer.clientY - secondaryPointer.clientY) ** 2);
		const scaleFactor = this._distance !== 0 ?
				distance / this._distance : 1;
		this._distance = distance;

		if (scaleFactor !== 1 && scaleFactor !== 0 && distance !== 0) {
			const focusPoint = [
				this._clientX / this.scale + this.scrollLeft,
				this._clientY / this.scale + this.scrollTop,
			];
			const focusPct = [
				this._clientX / this.viewportWidth,
				this._clientY / this.viewportHeight
			];

			let newScale = scaleFactor * this.scale;
			if ((this.scale > this.maximumScale && scaleFactor > 1) ||
					(this.scale < this.minimumScale && scaleFactor < 1))
				newScale = (1 + scaleFactor) * 0.5 * this.scale;
			this._scale = newScale;

			const newScrollLeft = focusPoint[0] - focusPct[0] *
					this.viewportWidth / this.scale;
			const newScrollTop = focusPoint[1] - focusPct[1] *
					this.viewportHeight / this.scale;

			this.scrollLeft = newScrollLeft;
			this.scrollTop = newScrollTop;
			this._clampScroll(true);
		}

		if (this.canScrollX)
			this.scrollLeft += this._deltaX / this.scale;
		if (this.canScrollY)
			this.scrollTop += this._deltaY / this.scale;

		clearTimeout(this._dragTimeout);
		this._dragTimeout = setTimeout(() => {
			for (const k in this._activePointers) {
				if (!this._activePointers.hasOwnProperty(k))
					continue;

				const pointer = this._activePointers[k];
				pointer.deltaX = 0;
				pointer.deltaY = 0;
			}
		}, 100);

		if (this.onboundschange)
			this.onboundschange(this);

		if ((Math.abs(primaryPointer.startX - this._clientX) > 5 ||
				Math.abs(primaryPointer.startY - this._clientY) > 5) &&
				(this.state === FlyerControllerState.POINTER_DOWN ||
				this.state === FlyerControllerState.DOUBLE_TAP_POINTER_DOWN)) {
			this.state = FlyerControllerState.DRAGGING;
		}
	}

	// Helper function to account for the differences between touch events.
	// Applies a function to the client coordinates of each touch involved
	// in the given.
	private _forEachPointer(e: FlyerPointerEvent, fn: (clientX: number, clientY: number, identifier: number) => void) {
		fn(e.clientX, e.clientY, e.pointerId);
	}

	private _handleDown = (e: FlyerPointerEvent) => {
		if (this._animation)
			this._animation.stop();

		// Add new pointers to the active pointer set. The first two pointers
		// to come down become the pointers used for drags and pinches. Additional
		// pointers are ignored until the first two come up, at which point they
		// may be promoted into `_fstPointer` or `_sndPointer`.
		this._forEachPointer(e, (clientX, clientY, identifier) => {
			this._activePointers[identifier.toString()] = {
				deltaX: 0,
				deltaY: 0,
				startX: clientX,
				startY: clientY,
				clientX: clientX,
				clientY: clientY
			};
			if (this._fstPointer === null)
				this._fstPointer = identifier;
			else if (this._sndPointer === null)
				this._sndPointer = identifier;
		});

		if (this._fstPointer === null)
			return;

		const numPointers = Object.keys(this._activePointers).length;
		const primaryPointer =
				this._activePointers[this._fstPointer.toString()];
		const secondaryPointer = numPointers > 1 ?
				this._activePointers[this._sndPointer.toString()] :
				primaryPointer;

		this._clientX =
				(primaryPointer.clientX + secondaryPointer.clientX) / 2;
		this._clientY =
				(primaryPointer.clientY + secondaryPointer.clientY) / 2;
		this._deltaX = 0;
		this._deltaY = 0;
		this._distance = Math.sqrt(
				(primaryPointer.clientX - secondaryPointer.clientX) ** 2 +
				(primaryPointer.clientY - secondaryPointer.clientY) ** 2);

		if (this._animation)
			this._animation.stop();

		if (numPointers === 1) {
			if (this.state === FlyerControllerState.WAITING_FOR_DOUBLE_TAP)
				this.state = FlyerControllerState.DOUBLE_TAP_POINTER_DOWN;
			else
				this.state = FlyerControllerState.POINTER_DOWN;
		} else
			this.state = FlyerControllerState.PINCH_DRAGGING;
	}

	private _handleWheel = (deltaX: number, deltaY: number) => {
		const boundsRatioX = (this.scrollLeft < 0 && deltaX < 0) ||
				(this.scrollLeft > this.scrollWidth - this.viewportWidth /
				this.scale && deltaX > 0) ? 0.2 : 1;
		const boundsRatioY = (this.scrollTop < 0 && deltaY < 0) ||
				(this.scrollTop > this.scrollHeight - this.viewportHeight /
				this.scale && deltaY > 0) ? 0.2 : 1;

		if (this.canScrollX && !this.canScrollY) {
			this.scrollLeft += deltaY * boundsRatioX / this.scale;
		}

		if (this.canScrollX)
			this.scrollLeft += deltaX * boundsRatioX / this.scale;
		if (this.canScrollY)
			this.scrollTop += deltaY * boundsRatioY / this.scale;

		this._clampScroll();
		if (this.onboundschange)
			this.onboundschange(this);

		this.state = FlyerControllerState.ANIMATING;
		clearTimeout(this._wheelTimeout);
		this._wheelTimeout = setTimeout(() => {
			this.state = FlyerControllerState.IDLE;
			if (this.onboundschange)
				this.onboundschange(this);
		}, 100);
	}

	private _clampScroll(onlyY = false) {
		if (!onlyY) {
			const centerOffsetX =
					Math.max(0, (this.viewportWidth -
											this.scrollWidth * this.scale) / 2);
			if (centerOffsetX !== 0)
				this.scrollLeft = -centerOffsetX / this.scale;
			else
				this.scrollLeft =
						Math.max(0, Math.min(this.scrollLeft, this.scrollRight));
		}

		const centerOffsetY =
				Math.max(0, (this.viewportHeight -
										this.scrollHeight * this.scale) / 2);
		if (centerOffsetY !== 0)
			this.scrollTop = -centerOffsetY / this.scale;
		else
			this.scrollTop =
					Math.max(0, Math.min(this.scrollTop, this.scrollBottom));
	}
}
